import { useEffect, useState } from 'react';
import 'katex/dist/katex.min.css';
import { ResponsiveFractalCanvas } from './ResponsiveFractalCanvas';
import { ViewportFractalCanvas } from './ViewportFractalCanvas';
import {
  coloringCloseups,
  deepZoomPresets,
  labyrinthineZoomPresets,
  overviewPresets,
  transfiniteZoomPresets,
} from './fractalPresets';
import { EXPLORER_PALETTE } from './fractalPalette';
import { buddhabrotColor } from './fractal_math';
import { codeSnippets, equations } from './staticContent';
import { renderedCode, renderedEquations } from './staticContent.generated';

export const REFERENCES = [
  { href: 'https://en.wikipedia.org/wiki/Mandelbrot_set', label: 'Wikipedia — Mandelbrot set' },
  { href: 'https://en.wikipedia.org/wiki/Plotting_algorithms_for_the_Mandelbrot_set', label: 'Wikipedia — Plotting algorithms for the Mandelbrot set' },
  { href: 'https://mathr.co.uk/web/deep-zoom.html', label: 'Claude Heiland-Allen’s Deep Zoom Theory and Practice' },
];

export const PAGE_SECTIONS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'world-overview', label: 'World overview' },
  { id: 'equation', label: 'Fractal equation' },
  {
    id: 'substructures',
    label: 'Sub-structures',
    subsections: [
      { id: 'substructures-overview', label: 'Overview' },
      { id: 'substructures-deep-zoom', label: 'Deep zoom' },
      { id: 'substructures-labyrinthine-zoom', label: 'Labyrinthine zoom' },
      { id: 'substructures-transfinite-zoom', label: 'Transfinite zoom' },
    ],
  },
  {
    id: 'coloring',
    label: 'Fractal coloring',
    subsections: [
      { id: 'coloring-escape-time', label: 'Escape time' },
      { id: 'coloring-field-shading', label: 'Field shading' },
      { id: 'coloring-orbit-traps', label: 'Orbit traps' },
      { id: 'coloring-buddhabrot', label: 'Buddhabrot' },
    ],
  },
  { id: 'about', label: 'About' },
];

// KaTeX and highlight.js run in scripts/generate-static-content.mjs, so the
// article ships as pre-rendered HTML and neither library reaches the browser.
const Equation = ({ name }) => {
  const html = renderedEquations[name];
  if (html === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Missing pre-rendered equation "${name}". Run \`yarn generate:content\`.`);
    }
    return <div className="equation" data-nosnippet="">{equations[name] ?? name}</div>;
  }
  return <div className="equation" data-nosnippet="" dangerouslySetInnerHTML={{ __html: html }} />;
};

const CodeBlock = ({ name }) => {
  const html = renderedCode[name];
  if (html === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Missing pre-rendered code snippet "${name}". Run \`yarn generate:content\`.`);
    }
    return <pre data-nosnippet=""><code className="language-javascript hljs">{codeSnippets[name] ?? name}</code></pre>;
  }
  return (
    <pre data-nosnippet="">
      <code
        className="language-javascript hljs"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </pre>
  );
};

const FractalSubsection = ({ entry, preferredRenderer }) => {
  const { section, headingId, title, description, preset } = entry;
  const isDeepCameraPreset = Boolean(preset.camera);
  const fallbackSrc = entry.fallbackSrc ?? preset.fallbackSrc ?? `./images/${section}.webp`;
  const fallbackAlt = entry.fallbackAlt ?? preset.fallbackAlt ?? `${title} — ${description}`;

  return (
    <section
      id={section}
      className="subsection fractal-subsection"
      data-section={section}
      aria-labelledby={headingId}
    >
      <h4 id={headingId}>{title}</h4>
      <p>{description}</p>
      <ResponsiveFractalCanvas
        {...preset}
        showControls
        deferUntilVisible={isDeepCameraPreset}
        preferredRenderer={preferredRenderer}
        fallbackSrc={fallbackSrc}
        fallbackAlt={fallbackAlt}
      />
    </section>
  );
};

const SidebarNav = () => {
  const [activeSection, setActiveSection] = useState(PAGE_SECTIONS[0].id);

  useEffect(() => {
    let scrollFrame = null;
    const selectSection = nextId => setActiveSection(prev => (prev === nextId ? prev : nextId));

    const updateSidebarState = () => {
      scrollFrame = null;
      if (document.body?.classList.contains('fullscreen-active')) return;

      const rects = PAGE_SECTIONS.map(section => {
        const el = document.getElementById(section.id);
        return el ? { section, rect: el.getBoundingClientRect() } : null;
      }).filter(Boolean);

      if (rects.length === 0) return;

      const hasLayout = rects.some(({ rect }) =>
        rect.top !== 0 || rect.bottom !== 0 || rect.height !== 0
      );
      if (!hasLayout) return;

      const viewportHeight = window.innerHeight || 800;
      const docHeight = document.documentElement?.scrollHeight || 0;
      if (window.scrollY > 0 && docHeight > viewportHeight &&
          window.scrollY + viewportHeight >= docHeight - 24) {
        selectSection(PAGE_SECTIONS[PAGE_SECTIONS.length - 1].id);
        return;
      }

      const threshold = Math.max(120, viewportHeight * 0.28);
      let currentSection = PAGE_SECTIONS[0];
      for (const { section, rect } of rects) {
        if (rect.top <= threshold) {
          currentSection = section;
        }
      }

      if (currentSection.subsections?.length) {
        let currentSubId = currentSection.subsections[0].id;
        for (const sub of currentSection.subsections) {
          const subEl = document.getElementById(sub.id);
          if (!subEl) continue;
          const subRect = subEl.getBoundingClientRect();
          const subHasLayout = subRect.top !== 0 || subRect.bottom !== 0 || subRect.height !== 0;
          if (subHasLayout && subRect.top <= threshold) {
            currentSubId = sub.id;
          }
        }
        selectSection(currentSubId);
        return;
      }

      selectSection(currentSection.id);
    };

    const handleScrollOrResize = () => {
      if (globalThis.IS_REACT_ACT_ENVIRONMENT || typeof requestAnimationFrame !== 'function') {
        updateSidebarState();
        return;
      }
      if (scrollFrame === null) {
        scrollFrame = requestAnimationFrame(updateSidebarState);
      }
    };

    updateSidebarState();
    window.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      if (scrollFrame !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(scrollFrame);
      }
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, []);

  return (
    <nav
      className="sidebar-nav"
      aria-label="Page sections"
      data-nosnippet=""
    >
      <ul className="sidebar-nav-list">
        {PAGE_SECTIONS.map(({ id, label, subsections }) => {
          const hasActiveChild = Boolean(subsections?.some(sub => sub.id === activeSection));
          const isActive = activeSection === id;
          return (
            <li key={id} className="sidebar-nav-item">
              <a
                href={`#${id}`}
                className={`sidebar-nav-link${isActive ? ' active' : ''}${hasActiveChild ? ' active-parent' : ''}`}
                aria-current={isActive ? 'location' : undefined}
                onClick={() => setActiveSection(subsections?.length ? subsections[0].id : id)}
              >
                {label}
              </a>
              {subsections?.length > 0 && (
                <ul className="sidebar-nav-sublist">
                  {subsections.map(sub => {
                    const isSubActive = activeSection === sub.id;
                    return (
                      <li key={sub.id} className="sidebar-nav-subitem">
                        <a
                          href={`#${sub.id}`}
                          className={`sidebar-nav-link sidebar-nav-sublink${isSubActive ? ' active' : ''}`}
                          aria-current={isSubActive ? 'location' : undefined}
                          onClick={() => setActiveSection(sub.id)}
                        >
                          {sub.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

const ITERATION_PALETTE = { ...EXPLORER_PALETTE, useMu: false };

const App = () => {
  const preferredRenderer = 'webgl';

  return <>
  <header>
    <h1>The Mandelbrot Set</h1>
  </header>

  <SidebarNav />

  <section id="intro" className="section" data-section="intro">
  <ResponsiveFractalCanvas
    width={1600}
    height={1600}
    fullWidth
    xRange={[-2.5, 1.1]}
    yRange={[-1.8, 1.8]}
    palette={EXPLORER_PALETTE}
    preferredRenderer={preferredRenderer}
    fallbackSrc="./images/mandelbrot-intro.webp"
    fallbackAlt="The Mandelbrot set in the complex plane with smooth navy, teal, ivory, and amber escape-time coloring"
    fetchPriority="high"
  />
  <div className="container">
    <p>
      If we plot the escape times for points in this complex plane series,
      we end up with one of the most famous visualizations in mathematics.
    </p>
    <Equation name="mandelbrot-with-i" />
  </div>
  </section>

  <section id="world-overview" className="section" data-section="world-overview" aria-labelledby="world-overview-heading">
  <div className="container">
    <h2 id="world-overview-heading">World overview</h2>
    <p>
      If we consider an escape radius of 2, the world also has a radius
      of 2, since any points starting outside of the world diverge right away.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={800}
      xRange={[-2.2, 2.2]}
      yRange={[-2.2, 2.2]}
      palette={EXPLORER_PALETTE}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/world-escape-radius.webp"
      fallbackAlt="Full Mandelbrot set world overview within the escape radius of 2 from -2.2 to 2.2"
    />

    <p>
      The set lies roughly on the real axis between &minus;2 and 0.5, with
      the imaginary extent reaching about ±1.25.
    </p>
    <p>
      The main cardioid is centered near the origin, with the period-2
      bulb to its left at about &minus;1.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={600}
      xRange={[-2.2, 0.8]}
      yRange={[-1.25, 1.25]}
      palette={EXPLORER_PALETTE}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/world-main-cardioid.webp"
      fallbackAlt="Mandelbrot set overview centered on the main cardioid and period-2 bulb from x = -2.2 to 0.8"
    />
  </div>
  </section>

  <section id="equation" className="section" data-section="equation" aria-labelledby="equation-heading">
  <div className="container">
    <h2 id="equation-heading">Mandelbrot set equation</h2>

    <p>The equation using complex numbers is:</p>
    <Equation name="mandelbrot-iteration" />

    <p>z is a complex number composed of real and imaginary parts:</p>
    <Equation name="complex-number" />

    <p>Fractal images are generated by varying the value of c:</p>
    <Equation name="complex-parameter" />

    <p>If we expand the complex squaring, the iteration reduces to:</p>
    <Equation name="real-part-iteration" />
    <Equation name="imaginary-part-iteration" />

    <section>
      <p>
        A common way of exploring fractals is by putting different starting (x0, y0)
        numbers into the equation and making note of whether the series:
      </p>
      <ul>
        <li>diverges to infinity or stays within a limit</li>
        <li>if the series diverges, we make note of:
          <ul>
            <li>the number of iterations until reaching the escape radius</li>
            <li>the distance from zero (0, 0) during the escape</li>
          </ul>
        </li>
      </ul>
    </section>
  </div>
  </section>

  <section id="substructures" className="section" data-section="substructures" aria-labelledby="substructures-heading">
  <div className="container">
    <h2 id="substructures-heading">Sub-structures</h2>
    <p>
      Beyond the main cardioid and bulbs, the fractal boundary is filled with
      spirals, scepters, dendrites, and miniature copies of the entire set.
      Zooming into its valleys and filaments uncovers new structures at every
      scale.
    </p>

    <div id="substructures-overview" className="substructures-subsection">
      <h3 className="substructures-subsection-title">Overview</h3>
      <p className="substructures-subsection-intro">
        At shallow magnifications, every pixel runs its own independent
        escape-time loop inside a WebGL fragment shader using native 32-bit GPU
        floats, with analytic checks that skip the interior of the main
        cardioid and period-2 bulb.
      </p>
      {overviewPresets.map(entry => (
        <FractalSubsection key={entry.section} entry={entry} preferredRenderer={preferredRenderer} />
      ))}
    </div>
  </div>

  <div
    id="substructures-deep-zoom"
    className="substructures-subsection substructures-subsection-deep"
    data-section="deep-zoom"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Deep zoom</h3>
      <p className="substructures-subsection-intro">
        Once the viewport narrows past 10<sup>-7</sup>, single-precision{' '}
        <code>float32</code> shaders lose subpixel spacing. These views step up
        to compensated double-single (<code>dsAdd</code> / <code>dsMul</code>)
        Dekker-split shaders and 53-bit significand <code>float64</code>{' '}
        workers to keep filaments razor-sharp down to the 10<sup>-15</sup> IEEE
        754 precision floor.
      </p>
      {deepZoomPresets.map(entry => (
        <FractalSubsection key={entry.section} entry={entry} preferredRenderer={preferredRenderer} />
      ))}
    </div>
  </div>

  <div
    id="substructures-labyrinthine-zoom"
    className="substructures-subsection substructures-subsection-labyrinthine"
    data-section="labyrinthine-zoom"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Labyrinthine zoom</h3>
      <p className="substructures-subsection-intro">
        Along these labyrinthine corridors past 10<sup>-16</sup>, standard 64-bit
        numbers can no longer tell neighboring pixels apart, and running custom
        big-number math for every pixel would bring the browser to a crawl.
        Instead, the renderer uses perturbation theory: it traces a single
        high-precision reference orbit through the center of the frame and
        measures how surrounding pixels drift from that path, using Bivariate
        Linear Approximation (BLA) jump tables to skip linear stretches and
        multi-center rebasing to repair local glitches.
      </p>
      {labyrinthineZoomPresets.map(entry => (
        <FractalSubsection key={entry.section} entry={entry} preferredRenderer={preferredRenderer} />
      ))}
    </div>
  </div>

  <div
    id="substructures-transfinite-zoom"
    className="substructures-subsection substructures-subsection-transfinite"
    data-section="transfinite-zoom"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Transfinite zoom</h3>
      <p className="substructures-subsection-intro">
        Crossing 10<sup>-38</sup> exhausts the exponent range of standard
        32-bit floats. Beyond this limit, the engine pairs a WebAssembly{' '}
        <code>libmpdec</code> arbitrary-precision decimal runtime with
        detached-exponent <code>floatExp</code> perturbation math and analytic
        hyperbolic interior certificates, evaluating thousands of iterations per
        pixel without underflowing.
      </p>
      {transfiniteZoomPresets.map(entry => (
        <FractalSubsection key={entry.section} entry={entry} preferredRenderer={preferredRenderer} />
      ))}
    </div>
  </div>
  </section>

  <section id="coloring" className="section" data-section="coloring" aria-labelledby="coloring-heading">
  <div className="container">
    <h2 id="coloring-heading">Fractal coloring</h2>

    <div id="coloring-escape-time" className="coloring-subsection">
      <h3 className="coloring-subsection-title">Escape time</h3>
      <p>
        Given a starting point (x0, y0), we can give each
        point a color by calculating the number of iterations it takes
        for the series to diverge, and the distance from zero when escaping.
      </p>
      <CodeBlock name="iterate-until-escape" />

      <p>
        We'll set the RGBA colors of every pixel in the image based
        on the number of iterations. Here's an example of a fiery yellow/red
        color palette by scaling just the green and alpha values of each pixel.
      </p>
      <CodeBlock name="iteration-color-palette" />

      <p>
        Here's a general function for drawing fractals onto canvas elements.
        Changing the iterateUntilEscape function will change the drawn fractal.
      </p>

      <CodeBlock name="draw-fractal-canvas" />

      <p>
        Here's the main cardioid colored based on
        number of iterations alone.
      </p>
      <ResponsiveFractalCanvas
        width={1600}
        height={1600}
        xRange={[-2.2, 0.8]}
        yRange={[-1.5, 1.5]}
        palette={ITERATION_PALETTE}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/main-cardioid-iteration-coloring.webp"
        fallbackAlt="Main cardioid of the Mandelbrot set colored using discrete integer iteration counts, showing contour bands"
      />
      <p>
        By considering escape distances on a logarithmic scale, we
        can use this extra information to smooth the color transitions.
      </p>
      <CodeBlock name="escape-time-coloring" />
      <p>
        Here's the same image with continuous escape counts and a smooth, repeating
        navy, teal, ivory, and amber palette. Extra iterations after escape soften
        contour seams, while a 1,024-iteration budget reveals finer boundary detail.
      </p>
      <ResponsiveFractalCanvas
        width={1600}
        height={1600}
        xRange={[-2.2, 0.8]}
        yRange={[-1.5, 1.5]}
        palette={EXPLORER_PALETTE}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/main-cardioid-smooth-coloring.webp"
        fallbackAlt="Main cardioid of the Mandelbrot set colored with continuous logarithmic escape-time smoothing"
      />
    </div>
  </div>

  <div
    id="coloring-field-shading"
    className="coloring-subsection coloring-subsection-field-shading"
    data-section="field-shading"
  >
    <div className="container">
      <h3 className="coloring-subsection-title">Field &amp; normal shading</h3>
      <p>
        Escape-time palettes only read how quickly an orbit leaves the escape
        disk, discarding the angle at which it exits and how fast neighboring
        orbits diverge. Incorporating the escaped coordinate z<sub>n</sub> and
        its running complex derivative dz<sub>n</sub>/dc turns the exterior
        into a coordinate field with equipotential contours, external-ray
        cells, and three-dimensional surface relief.
      </p>
      <p>
        Modulating the continuous potential with periodic waves in both escape
        time and escape angle overlays equipotential rings and external-ray
        spokes across the 1, 2, 3, 5, 8, 13, and 21 Fibonacci bulb wakes on the
        upper shoulder of the main cardioid.
      </p>
      <ResponsiveFractalCanvas
        {...coloringCloseups.equipotentialExternalRays}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/equipotential-external-rays.webp"
        fallbackAlt="Fibonacci bulb wakes along the upper shoulder of the Mandelbrot cardioid colored with equipotential rings and external-ray spokes"
      />
      <p>
        At a higher bailout radius, partitioning each escaped point by the
        binary sign of its escape angle and fractional iteration level reveals
        the Douady-Hubbard cellular parquet mosaic spiralling into Seahorse
        Valley.
      </p>
      <ResponsiveFractalCanvas
        {...coloringCloseups.binaryDecompositionParquetMosaic}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/binary-decomposition-parquet-mosaic.webp"
        fallbackAlt="Douady-Hubbard binary angle decomposition parquet mosaic spiralling into Seahorse Valley"
      />
      <p>
        Tracking the running complex derivative alongside the orbit yields the
        exterior surface normal z<sub>n</sub> / z&prime;<sub>n</sub>. Applying
        directional Lambertian lighting to that normal field highlights the 25
        spokes of an upside-down seahorse in three-dimensional relief.
      </p>
      <ResponsiveFractalCanvas
        {...coloringCloseups.derivativeNormalSeahorse}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/derivative-normal-seahorse.webp"
        fallbackAlt="Upside-down 25-spoke seahorse illuminated with complex-derivative 3D Lambertian normal shading"
      />
    </div>
  </div>

  <div
    id="coloring-orbit-traps"
    className="coloring-subsection coloring-subsection-orbit-traps"
    data-section="orbit-traps"
  >
    <div className="container">
      <h3 className="coloring-subsection-title">Orbit traps &amp; stripe averages</h3>
      <p>
        Instead of inspecting only the final iterate after escape, orbit-trap
        and average colorings accumulate measurements across every step of the
        trajectory z<sub>2</sub>, &hellip;, z<sub>n</sub>. Geometric traps
        record how closely an orbit approaches a target curve in the complex
        plane, while angular averages blend trigonometric samples along the
        entire path.
      </p>
      <p>
        Measuring each orbit&rsquo;s closest approach to the real or imaginary
        axis produces Pickover stalks that trace the single simply connected
        zigzag filament threading through Seahorse Valley&rsquo;s 25-spoke
        hubs.
      </p>
      <ResponsiveFractalCanvas
        {...coloringCloseups.pickoverStalkOrbitTraps}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/pickover-stalk-orbit-traps.webp"
        fallbackAlt="Pickover stalk axis orbit traps illuminating the simply connected zigzag filament in Seahorse Valley"
      />
      <p>
        Replacing the coordinate axes with the circle |z| = 1.25 traps orbits
        along a ring, weaving a glowing cellular astrolabe web across the
        four-armed spirals of Quad Spiral Valley.
      </p>
      <ResponsiveFractalCanvas
        {...coloringCloseups.orbitalRingTrapCellularWeb}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/orbital-ring-trap-cellular-web.webp"
        fallbackAlt="Circular ring orbit trap at |z| = 1.25 weaving a cellular web across Quad Spiral Valley"
      />
      <p>
        Jussi H&auml;rk&ouml;nen&rsquo;s trigonometric stripe-average coloring
        averages sin(6 arg z<sub>n</sub>) over all escaped iterates and
        interpolates the final step by fractional escape count, revealing
        electric field streams spiralling into the terminal Misiurewicz point
        at c = i.
      </p>
      <ResponsiveFractalCanvas
        {...coloringCloseups.stripeLightningVortex}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/stripe-average-lightning-vortex-at-c-i.webp"
        fallbackAlt="Stripe-average coloring at the terminal Misiurewicz point c = i showing spiraling electric field streams"
      />
    </div>
  </div>

  <div
    id="coloring-buddhabrot"
    className="coloring-subsection coloring-subsection-buddhabrot"
    data-section="buddhabrot"
  >
    <div className="container">
      <h3 className="coloring-subsection-title">Buddhabrot</h3>
      <p>
        While escape-time coloring only cares how long a point takes to escape,
        a Buddhabrot tracks every step along the way and brightens pixels that
        get visited the most. Introduced by Melinda Green, this technique—often
        called a Nebulabrot when it layers three orbit-density exposure tiers—combines
        fast, medium, and slow-escaping trajectories like astronomical false-color exposures
        into atmospheric purple, luminous electric sapphire, and radiant ice-white channels.
      </p>
      <p>
        Tracing those paths fills the dark interior of the main cardioid and
        period-2 bulb with an ethereal lotus-like silhouette and a glowing cyan-white
        caustic crown along the boundary.
      </p>
      <ResponsiveFractalCanvas
        width={1600}
        height={1600}
        xRange={[-2.2, 0.8]}
        yRange={[-1.5, 1.5]}
        colorFunc={buddhabrotColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/main-cardioid-buddhabrot.webp"
        fallbackAlt="Ethereal celestial Buddhabrot render of the Mandelbrot set colored by orbit visit frequency in deep violet, electric sapphire, and radiant white"
      />
      <p>
        Along the western needle from the mini-Mandelbrot at &minus;1.75 toward
        the period-2 bulb, a chain of island copies glows with electric dendrite
        spokes against a deep cosmic nebula.
      </p>
      <ResponsiveFractalCanvas
        width={1600}
        height={960}
        xRange={[-1.86, -1.20]}
        yRange={[-0.198, 0.198]}
        colorFunc={buddhabrotColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/western-needle-buddhabrot.webp"
        fallbackAlt="Wide Buddhabrot view along the western needle from x = -1.86 to -1.20 showing the mini-Mandelbrot, period-doubling bulbs, and electric dendrite filaments"
      />
      <p>
        Across the neck between the period-2 bulb and the main cardioid,
        trajectories fold into symmetric rows of amethyst, cobalt, and electric cyan
        parabolic blades.
      </p>
      <ResponsiveFractalCanvas
        width={1600}
        height={1600}
        xRange={[-0.86, -0.44]}
        yRange={[-0.21, 0.21]}
        colorFunc={buddhabrotColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/seahorse-caustics-buddhabrot.webp"
        fallbackAlt="Buddhabrot render of curved amethyst, cobalt, and electric cyan parabolic caustic blades converging across the Seahorse Valley neck"
      />
    </div>
  </div>
  </section>

    <section id="about" className="section" data-section="about" aria-labelledby="about-heading">
      <div className="container">
        <h2 id="about-heading">About</h2>
        <p>
          All fractals on the page are interactive and rendered when
          they're in view. Every image can be expanded into full-screen mode
          using the button in its bottom-right corner. Full-screen views also
          display live complex-plane coordinates and let you undo camera steps or
          reset to the starting view.
        </p>
        <p>
          As you zoom deeper, each canvas automatically switches to more
          sophisticated rendering pipelines to stay fast and avoid pixelation.
          Shallow views run per-pixel escape loops in 32-bit WebGL fragment
          shaders.
        </p>
        <p>
          Zooming past 10<sup>-7</sup> switches to compensated double-single
          shaders and 64-bit workers. Beyond 10<sup>-16</sup>, the renderer
          switches to perturbation theory and arbitrary-precision decimal math so
          you can keep zooming without losing detail.
        </p>
        <p data-nosnippet="">Fractal image controls when hovering</p>
        <ul data-nosnippet="">
          <li>shift + scroll mouse wheel — zoom in/out</li>
          <li>double-click (or shift + double-click) — zoom in/out at cursor</li>
          <li>+ / &minus; keys — zoom in/out</li>
          <li>pinch — zoom in/out</li>
          <li>click and drag — pan the view</li>
          <li>two-finger swipe — pan the view on touchscreens</li>
          <li>one-finger swipe — scroll the page</li>
          <li>cmd-z — undo the last camera action</li>
          <li>0 — reset view to its initial position</li>
        </ul>
      </div>
    </section>

    <footer>
      <div className="container">
        <h2 id="references-heading">References</h2>
        <ul>
          {REFERENCES.map(({ href, label }) => (
            <li key={href}><a href={href}>{label}</a></li>
          ))}
        </ul>
      </div>
    </footer>

    <ViewportFractalCanvas preferredRenderer={preferredRenderer} />
</>;
};

export default App;
